import { useCallback, useEffect, useState } from "react";
import { IErrorData } from "../api/services/types";
import { convertException } from "../utils/convertException";

interface IResult<T,P> {
  data: T | null;
  isLoading: boolean;
  error: IErrorData | null;
  execute: (props?: P) => Promise<void>;
}

export const useFetch = <T,P>(
  func: (props?: P) => Promise<T>,
  canRunRequest: boolean = true
): IResult<T,P> => {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<IErrorData | null>(null);

  const execute = useCallback(async (props?: P) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await func(props);
      setData(result);
    } catch (err) {
      setError(convertException(err));
    } finally {
      setIsLoading(false);
    }
  }, [func]);

  useEffect(() => {
    if (canRunRequest) {
      execute();
    }
  }, [execute, canRunRequest]);

  return { data, isLoading, error, execute };
};
